import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { PAGE_COMPONENTS } from '@workbuddy/ui/blocks/page';
import { CONTENT_LIST_COMPONENTS } from '@workbuddy/ui/components/content-list';
import { HttpClient, httpResource } from '@angular/common/http';
import { catchError, of, tap } from 'rxjs';
import { AuthTokenService } from '@jms/utils/util.authentication';
import { UtilService } from '@jms/utils/util.services';

@Component({
    selector: 'consent',
    templateUrl: './consent.component.html',
    styleUrls: ['./consent.component.less'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        PAGE_COMPONENTS,
        ButtonComponent,
        CONTENT_LIST_COMPONENTS
    ],
})
export class ConsentComponent {

    private http = inject(HttpClient);
    private authTokenService = inject(AuthTokenService);

    private $validate = httpResource(() => `/api/oauth2/consent/validate`);

    protected $loading = computed(() => this.$validate.isLoading());
    protected $error = computed(() => this.$validate.error() ? 'Invalid or expired authorisation request' : null);

    protected $clientApp = computed(() => {

        const validate = this.$validate.value() as any;
        return {
            name: validate?.client?.name,
            logoUrl: validate?.client?.image?.path
        };
    });

    protected $user = computed(() => {

        const token = this.authTokenService.getToken();
        if (!token) return { name: '', imageUrl: '' };
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return { name: payload.name ?? '', imageUrl: payload.image ?? '' };
        } catch {
            return { name: '', imageUrl: '' };
        }
    });

    protected $scopeGroups = computed(() => {

        const validate = this.$validate.value() as any;
        return validate?.scopeGroups || [];
    });

    protected approve(): void {

        this.http
            .post<{ location: string }>('/api/oauth2/consent/approve', {})
            .pipe(
                catchError(() => of(null)),
                tap((response) => {
                    if (response?.location) UtilService.navigate(`/api/oauth2/success?redirect=${encodeURIComponent(response.location)}`);
                })
            )
            .subscribe();
    }

    protected deny(): void {

        this.http
            .post<{ location: string }>('/api/oauth2/consent/reject', {})
            .pipe(
                catchError(() => of(null)),
                tap((response) => {
                    if (response?.location) UtilService.navigate(response.location);
                })
            )
            .subscribe();
    }

    private static readonly SCOPE_GROUP_ICONS: Record<string, string> = {
        'Jobs': 'fa-solid fa-briefcase',
        'CRM': 'fa-solid fa-address-book',
        'Settings': 'fa-solid fa-gear',
        'Webhooks': 'fa-solid fa-bolt',
    };

    protected getScopeGroupIcon(group: string): string {

        return ConsentComponent.SCOPE_GROUP_ICONS[group] ?? 'fa-solid fa-shield-check';
    }

    protected getScopeIcon(scopeName: string): string {

        if (scopeName?.includes(':write') || scopeName?.includes(':admin')) return 'fa-solid fa-pen scope-icon scope-icon-write';
        return 'fa-solid fa-eye scope-icon scope-icon-read';
    }
}
